// src/pages/BlogPage.js

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchPost, renderStrapiRichText, STRAPI_URL } from '../api/strapi';
import Footer from '../components/Footer';
import { Helmet } from 'react-helmet-async';
import './BlogPage.css';

// ---------- helpers ----------
const isAbsoluteUrl = (url) => /^https?:\/\//i.test(url || '');

const toAbsolute = (url) => {
  if (!url) return '';
  return isAbsoluteUrl(url) ? url : `${(STRAPI_URL || '').replace(/\/$/, '')}${url.startsWith('/') ? '' : '/'}${url}`;
};

// Extract a short snippet (first paragraph-like text) from Strapi rich text blocks
const getExcerptFromBlocks = (blocks, maxLen = 180) => {
  if (!Array.isArray(blocks)) return '';
  // Common Strapi Rich Text (v4/v5) shapes: paragraph nodes under content/children, or simple blocks with 'type'
  for (const block of blocks) {
    // Case 1: direct paragraph-like block
    if (typeof block?.type === 'string' && /paragraph|text/i.test(block.type)) {
      const txt = (block.text || block.children?.map((c) => c.text).join(' ') || '').trim();
      if (txt) return txt.length > maxLen ? `${txt.slice(0, maxLen).trim()}…` : txt;
    }
    // Case 2: rich-text content array
    const maybeNodes = block?.content || block?.children;
    if (Array.isArray(maybeNodes)) {
      for (const node of maybeNodes) {
        if (typeof node?.type === 'string' && /paragraph|text/i.test(node.type)) {
          const txt = (node.text || node.children?.map((c) => c.text).join(' ') || '').trim();
          if (txt) return txt.length > maxLen ? `${txt.slice(0, maxLen).trim()}…` : txt;
        }
      }
    }
  }
  return '';
};

// Safely render values, preventing raw objects from crashing the component
const renderSafely = (value, fieldName = 'value') => {
  if (typeof value === 'object' && value !== null && !React.isValidElement(value)) {
    if (value.name && value.imageUrl) {
      return `[Author: ${value.name}]`;
    }
    try {
      return JSON.stringify(value).slice(0, 100) + '...';
    } catch {
      return '[Object]';
    }
  }
  return value == null ? '' : value;
};

export default function BlogPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost(slug)
      .then((p) => {
        if (!p) throw new Error('Post not found');
        setPost(p);
      })
      .catch((err) => {
        console.error('Error fetching post:', err);
        setError(err.message);
      });
  }, [slug]);

  if (error) {
    return <div className="blogError">Error: {renderSafely(error, 'Error message')}</div>;
  }
  if (!post) {
    return <div className="blogLoading">Loading…</div>;
  }

    // ---------- SEO / Sharing values ----------
  const siteOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const url = `${siteOrigin}/blog/${post.slug}`;
  const title = String(renderSafely(post.title, 'Helmet title')) || 'Article';
  const excerpt =
    getExcerptFromBlocks(post.contentBlocks) ||
    String(renderSafely(post.summary || post.title, 'Helmet description'));
  const image = toAbsolute(post.imageUrl);
  const authorName = post?.author?.name ? String(post.author.name) : '';

  let published;
  if (post?.date) {
    const d = new Date(post.date);
    if (!isNaN(d.getTime())) {
      published = d.toISOString();
    }
  }

  // JSON-LD (optional but nice for search engines)
  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    image: image ? [image] : undefined,
    author: authorName ? { '@type': 'Person', name: authorName } : undefined,
    datePublished: published,
    mainEntityOfPage: url,
    description: excerpt,
  };

  return (
    <main className="blogPageContainer">
      <Helmet>
        {/* Basic SEO */}
        <title>{title}</title>
        <meta name="description" content={excerpt} />
        <link rel="canonical" href={url} />

        {/* Open Graph (for LinkedIn, Facebook, Slack, iMessage, etc.) */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={excerpt} />
        {image && <meta property="og:image" content={image} />}
        {image && <meta property="og:image:alt" content={title} />}
        {published && <meta property="article:published_time" content={published} />}
        {authorName && <meta property="article:author" content={authorName} />}

        {/* Twitter Card */}
        <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={excerpt} />
        {image && <meta name="twitter:image" content={image} />}

        {/* JSON-LD Article */}
        <script type="application/ld+json">
          {JSON.stringify(articleLd)}
        </script>
      </Helmet>

      <article className="blogPage">
        <h1 className="postTitle">{renderSafely(post.title, 'Post Title')}</h1>

        <div className="postMeta">
          {post.author?.imageUrl && (
            <img
              className="authorImage"
              src={post.author.imageUrl}
              alt={renderSafely(post.author.name, 'Author Image Alt')}
            />
          )}
          <span className="authorName">
            {renderSafely(post.author.name, 'Author Name')}
          </span>
          <span className="postDate"> {' '}| {renderSafely(post.date, 'Post Date')}</span>
        </div>

        {post.imageUrl && (
          <div className="postHero">
            <img src={post.imageUrl} alt={renderSafely(post.title, 'Hero Image Alt')} />
          </div>
        )}

        <div className="postContent">
          {renderStrapiRichText(post.contentBlocks)}
        </div>
      </article>

      <div className="footerFullWidth">
        <Footer />
      </div>
    </main>
  );
}
